import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('chartaudit_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle auth errors (only explicit 401 — not timeouts/network failures)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    if (status === 401 && err.config?.url !== '/auth/login') {
      localStorage.removeItem('chartaudit_token');
      localStorage.removeItem('chartaudit_refresh_token');
      localStorage.removeItem('chartaudit_user');
      window.location.href = '/login';
    }
    if (status === 429) {
      const retryAfter = err.response?.headers?.['retry-after'];
      err.rateLimited = true;
      err.retryAfter = retryAfter ? Number(retryAfter) : 900;
    }
    return Promise.reject(err);
  }
);

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const getProfile = () => api.get('/auth/me');
export const uploadLogo = (formData) =>
  api.post('/auth/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Documents
export const uploadDocument = (formData) =>
  api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getDocuments = () => api.get('/documents');

// Audits
export const startAudit = (data) => api.post('/audits', data);
export const getAudits = () => api.get('/audits');
export const getAudit = (id) => api.get(`/audits/${id}`, { timeout: 20000 });
export const downloadAuditPdf = (id) => api.get(`/audits/${id}/pdf`, { responseType: 'blob' });
export const uploadSupplemental = (auditId, formData) =>
  api.post(`/audits/${auditId}/supplemental`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getSupplementalDocs = (auditId) => api.get(`/audits/${auditId}/supplemental`);

// Dashboard stats (PDGM, HHCAPS, weaknesses, recommendations)
export const getDashboardStats = () => api.get('/audits/stats/dashboard');
export const downloadRecommendationPdf = (id) => api.get(`/audits/recommendations/${id}/pdf`, { responseType: 'blob' });

// Admin
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminAgencies = () => api.get('/admin/agencies');
export const toggleAgencyStatus = (id, isActive) => api.patch(`/admin/agencies/${id}/status`, { isActive });
export const getPendingUsers = () => api.get('/admin/pending-users');
export const approveUser = (id) => api.patch(`/admin/users/${id}/approve`);
export const rejectUser = (id) => api.patch(`/admin/users/${id}/reject`);
export const getAdminAudits = (params = {}) => api.get('/admin/audits', { params });
export const getAgencyAuditSettings = (id) => api.get(`/admin/agencies/${id}/audit-settings`);
export const updateAgencyAuditSettings = (id, data) => api.patch(`/admin/agencies/${id}/audit-settings`, data);
export const getAgencyRecommendations = (id) => api.get(`/admin/agencies/${id}/recommendations`);
export const downloadAdminRecommendationPdf = (id) => api.get(`/admin/recommendations/${id}/pdf`, { responseType: 'blob' });
export const getAgencyWeaknessSummary = (id) => api.get(`/admin/agencies/${id}/weakness-summary`);
export const deleteAgency = (id) => api.delete(`/admin/agencies/${id}`);
export const getAdminReportsSummary = (params = {}) => api.get('/admin/reports/summary', { params });
export const getAdminRecommendationReports = () => api.get('/admin/reports/recommendations');
export const getAdminCostSummary = () => api.get('/admin/costs/summary');

// Support & feedback
export const submitTicket = (data) => api.post('/support/tickets', data);
export const getAdminTickets = (params = {}) => api.get('/support/tickets', { params });
export const getAdminTicket = (id) => api.get(`/support/tickets/${id}`);
export const updateTicketStatus = (id, data) => api.patch(`/support/tickets/${id}/status`, data);
export const replyToTicket = (id, message) => api.post(`/support/tickets/${id}/reply`, { message });
export const getAdminErrorLogs = (params = {}) => api.get('/support/errors', { params });
export const resolveErrorLog = (id, notes) => api.patch(`/support/errors/${id}/resolve`, { notes });

// Payments & Subscriptions
export const createSetupIntent = () => api.post('/payments/setup-intent');
export const confirmPaymentMethod = () => api.post('/payments/confirm-payment-method');
export const chargeReport = (auditId) => api.post('/payments/charge-report', { auditId });
export const getPaymentStatus = () => api.get('/payments/status');
export const subscribeToPlan = () => api.post('/payments/subscribe');
export const confirmSubscription = (subscriptionId) => api.post('/payments/confirm-subscription', { subscriptionId });
export const cancelSubscription = () => api.post('/payments/cancel-subscription');
export const switchPlan = (plan) => api.post('/payments/switch-plan', { plan });

export default api;
